import { z } from 'zod';
import { MATERIAL_MAX_BYTES, materialKeySchema } from '../generation/studyMaterial';
import { languageSchema, textSchema, timestampSchema } from '../task/task';
import {
  jobMethodSchema,
  jobRequestSchema,
  jobSchema,
  jobTaskCountSchema,
  microUsdSchema,
  writeModelSchema,
} from './job';

/** The generation jobs collection path (QZ-D48). */
export const JOBS_PATH = '/jobs';

/** A single job's path (QZ-D48). */
export const jobPath = (jobId: string): string => `/jobs/${encodeURIComponent(jobId)}`;

/** A single job's cancel path (QZ-D57). */
export const jobCancelPath = (jobId: string): string => `${jobPath(jobId)}/cancel`;

/** The router pattern matching `jobPath` (QZ-D48). */
export const JOB_PATH_PATTERN = '/jobs/:jobId';

/** The router pattern matching `jobCancelPath` (QZ-D57). */
export const JOB_CANCEL_PATH_PATTERN = '/jobs/:jobId/cancel';

/** The number of jobs returned by one page of `GET /jobs` (QZ-D55). */
export const JOB_PAGE_SIZE = 25;

/** The spend totals path (QZ-D55). */
export const SPEND_PATH = '/spend';

/**
 * The body accepted by `POST /jobs` (QZ-D48, PDG-D2). `method` is optional with no zod
 * `.default`: absent means `'prompt'`, which `newJob` applies. A `.default` would make `method`
 * required in `CreateJobRequest`, breaking every existing request literal and
 * `generateJob_test`'s `toEqual` on the parsed body.
 */
export const createJobRequestSchema = z
  .object({
    method: jobMethodSchema.optional(),
    request: jobRequestSchema.optional(),
    instructions: jobRequestSchema.optional(),
    materialKey: materialKeySchema.optional(),
    materialName: textSchema.max(200).optional(),
    language: languageSchema,
    taskCount: jobTaskCountSchema,
    /** Omitted means `DEFAULT_WRITE_MODEL`. */
    writeModel: writeModelSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const method = value.method ?? 'prompt';
    if (method === 'prompt') {
      if (value.request === undefined) {
        ctx.addIssue({ code: 'custom', path: ['request'], message: 'A request is required.' });
      }
      if (value.materialKey !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['materialKey'],
          message: 'A prompt job must not have a material key.',
        });
      }
      if (value.materialName !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['materialName'],
          message: 'A prompt job must not have a material name.',
        });
      }
      if (value.instructions !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['instructions'],
          message: 'A prompt job must not have instructions.',
        });
      }
    } else {
      if (value.materialKey === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['materialKey'],
          message: 'A material key is required.',
        });
      }
      if (value.materialName === undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['materialName'],
          message: 'A material name is required.',
        });
      }
      if (value.request !== undefined) {
        ctx.addIssue({
          code: 'custom',
          path: ['request'],
          message: 'A PDF job must not have a request.',
        });
      }
    }
  });
export type CreateJobRequest = z.infer<typeof createJobRequestSchema>;

/** The PDF study material upload path (PDG-D2). */
export const MATERIAL_UPLOAD_PATH = '/jobs/material';

/** A single job's study material path (PDG-D2). */
export const jobMaterialPath = (jobId: string): string => `${jobPath(jobId)}/material`;

/** The router pattern matching `jobMaterialPath` (PDG-D2). */
export const JOB_MATERIAL_PATH_PATTERN = '/jobs/:jobId/material';

/**
 * The body accepted by `POST /jobs/material` (PDG-D6). The name's `.pdf` ending is not checked
 * here; that is the service's job (issue 08).
 */
export const createMaterialUploadRequestSchema = z
  .object({
    name: textSchema.max(200),
    sizeBytes: z.number().int().positive().max(MATERIAL_MAX_BYTES),
    contentType: z.literal('application/pdf'),
  })
  .strict();
export type CreateMaterialUploadRequest = z.infer<typeof createMaterialUploadRequestSchema>;

/** The body returned by `POST /jobs/material` (PDG-D6). */
export const materialUploadResponseSchema = z
  .object({
    key: materialKeySchema,
    uploadUrl: z.url(),
    expiresAt: timestampSchema,
  })
  .strip();
export type MaterialUploadResponse = z.infer<typeof materialUploadResponseSchema>;

/** The body returned by `GET /jobs/:jobId/material` (PDG-D6). */
export const jobMaterialResponseSchema = z
  .object({
    url: z.url(),
    expiresAt: timestampSchema,
  })
  .strip();
export type JobMaterialResponse = z.infer<typeof jobMaterialResponseSchema>;

/** The query string accepted by `GET /jobs` (QZ-D55). */
export const listJobsQuerySchema = z.object({ cursor: z.string().min(1).optional() }).strict();
export type ListJobsQuery = z.infer<typeof listJobsQuerySchema>;

/** The body returned by `GET /jobs` (QZ-D55). */
export const listJobsResponseSchema = z
  .object({
    jobs: z.array(jobSchema),
    nextCursor: z.string().min(1).optional(),
  })
  .strip();
export type ListJobsResponse = z.infer<typeof listJobsResponseSchema>;

/** The body returned by `GET /spend` (QZ-D55). */
export const spendResponseSchema = z
  .object({
    allTimeMicroUsd: microUsdSchema,
    thisMonthMicroUsd: microUsdSchema,
    month: z.string().regex(/^\d{4}-\d{2}$/),
  })
  .strip();
export type SpendResponse = z.infer<typeof spendResponseSchema>;
